import { createFileRoute, Link, useLocation } from '@tanstack/react-router'
import {
  ArrowRight,
  Building2,
  CheckCircle2,
  CloudCog,
  DatabaseZap,
  Gauge,
  MapPin,
  Network,
  ShieldCheck,
  Workflow,
  Wrench,
  type LucideIcon,
} from 'lucide-react'
import { motion } from 'motion/react'
import { useTranslation } from 'react-i18next'
import { LandingPageLayout } from '@/components/landing/LandingPageLayout'
import { SectionScroller } from '@/components/landing/SectionScroller'
import { GridSpotlight } from '@/components/ui/grid-spotlight'
import { designSystem } from '@/lib/design-system'
import { getLocaleFromPath, withLocalePath } from '@/lib/i18n-utils'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/{-$locale}/about')({
  head: () => ({
    meta: [
      { title: 'About — Ponte Solutions' },
      {
        name: 'description',
        content:
          'Ponte Solutions is a founder-led consultancy for connected products, data ingestion, analytics, and action intelligence around hardware already in the field.',
      },
      { property: 'og:title', content: 'About — Ponte Solutions' },
      {
        property: 'og:description',
        content:
          'Ponte Solutions is a founder-led consultancy for connected products, data ingestion, analytics, and action intelligence around hardware already in the field.',
      },
    ],
  }),
  component: AboutPage,
})

type AboutPillar = {
  key: string
  icon: LucideIcon
  title: string
  description: string
  points: string[]
}

type AboutCopy = {
  scrollerLabels: string[]
  heroBadge: string
  heroLead: string
  heroPrimary: string
  heroSecondary: string
  panelTitle: string
  panelSubtitle: string
  panelSteps: Array<{ label: string; value: string; icon: LucideIcon }>
  panelStats: Array<{ value: string; label: string }>
  shiftEyebrow: string
  shiftTitle: string
  shiftLead: string
  shiftParagraphs: string[]
  modelEyebrow: string
  modelTitle: string
  modelLead: string
  stages: Array<{
    eyebrow: string
    title: string
    text: string
    icon: LucideIcon
  }>
  principlesEyebrow: string
  principlesTitle: string
  principlesLead: string
  principles: Array<{ title: string; text: string; icon: LucideIcon }>
  ctaEyebrow: string
}

const enCopy: AboutCopy = {
  scrollerLabels: ['Position', 'Gap', 'Model', 'Principles', 'Start'],
  heroBadge: 'Founder-led connected product engineering',
  heroLead:
    'Ponte is small by design: senior technical judgement, direct communication, and the full connected-product stack from device data to service action.',
  heroPrimary: 'Book an Audit',
  heroSecondary: 'See Services',
  panelTitle: 'Ponte operating model',
  panelSubtitle: 'A focused route from installed hardware to service value.',
  panelSteps: [
    {
      label: 'Field data',
      value: 'Device, edge, protocol, and site reality',
      icon: Network,
    },
    {
      label: 'Data product',
      value: 'Clean models, roles, dashboards, and alerts',
      icon: DatabaseZap,
    },
    {
      label: 'Service action',
      value: 'Cases, workflows, automation, and reporting',
      icon: Workflow,
    },
  ],
  panelStats: [
    { value: 'DACH / EU', label: 'client focus' },
    { value: 'Cloud agnostic', label: 'Azure, AWS, Google Cloud' },
    { value: 'Founder-led', label: 'senior ownership' },
  ],
  shiftEyebrow: 'Why Ponte exists',
  shiftTitle:
    'The product is already strong. The service layer is the next advantage.',
  shiftLead:
    'Most hardware companies already have the hard part: a trusted physical product in the field. The next gap is visibility, customer access, service workflow, and business-system integration.',
  shiftParagraphs: [
    'Ponte closes that gap without pretending to be the manufacturer. We build the connected-product layer around products your company already designs, sells, installs, and services.',
    'The result is not a generic portal or a cloud demo. It is a practical operating layer that helps product, service, and management teams use device data in daily decisions.',
  ],
  modelEyebrow: 'How we engage',
  modelTitle: 'Start practical, prove the route, then scale.',
  modelLead:
    'The best connected-product programs do not start with a massive platform build. They start with a clear product line, real field data, and one valuable service workflow.',
  stages: [
    {
      eyebrow: '01',
      title: 'Connected Product Audit',
      text: 'We map the product line, device data, field constraints, users, service process, business systems, and first realistic value case.',
      icon: Gauge,
    },
    {
      eyebrow: '02',
      title: 'Pilot With Real Data',
      text: 'We build a narrow, working route: ingest the signal, process it into usable context, and put it in front of the team that needs to act.',
      icon: Wrench,
    },
    {
      eyebrow: '03',
      title: 'Service Platform Foundation',
      text: 'We harden identity, roles, dashboards, automations, reporting, and runbooks so the system can become part of operations.',
      icon: CloudCog,
    },
  ],
  principlesEyebrow: 'Operating principles',
  principlesTitle: 'A consultancy built for the messy middle.',
  principlesLead:
    'Ponte sits between hardware, cloud, analytics, and service operations. That only works when the engagement stays practical and technically accountable.',
  principles: [
    {
      title: 'Direct senior ownership',
      text: 'You work with the people responsible for architecture and delivery, not a rotating layer of account management.',
      icon: ShieldCheck,
    },
    {
      title: 'Cloud path follows product strategy',
      text: 'Azure, AWS, Google Cloud, hybrid, or existing infrastructure: the architecture follows the product and operating model.',
      icon: CloudCog,
    },
    {
      title: 'Business systems are part of the design',
      text: 'Device data should reach service, CRM, ERP, BI, and customer workflows instead of becoming another isolated cloud silo.',
      icon: Workflow,
    },
    {
      title: 'Handover is engineered',
      text: 'Runbooks, monitoring, cost controls, and clear ownership boundaries are part of delivery, not optional cleanup.',
      icon: CheckCircle2,
    },
  ],
  ctaEyebrow: 'Start with the installed base',
}

const deCopy: AboutCopy = {
  scrollerLabels: ['Position', 'Lücke', 'Modell', 'Prinzipien', 'Start'],
  heroBadge: 'Founder-led Engineering für Connected Products',
  heroLead:
    'Ponte ist bewusst klein: Senior-Entscheidungen, direkte Kommunikation und der komplette Connected-Product-Stack von Gerätedaten bis Serviceaktion.',
  heroPrimary: 'Audit buchen',
  heroSecondary: 'Services ansehen',
  panelTitle: 'Ponte Arbeitsmodell',
  panelSubtitle:
    'Ein fokussierter Weg von installierter Hardware zu Servicewert.',
  panelSteps: [
    {
      label: 'Felddaten',
      value: 'Geräte, Edge, Protokolle und Standortrealität',
      icon: Network,
    },
    {
      label: 'Datenprodukt',
      value: 'Saubere Modelle, Rollen, Dashboards und Alerts',
      icon: DatabaseZap,
    },
    {
      label: 'Serviceaktion',
      value: 'Cases, Workflows, Automatisierung und Reporting',
      icon: Workflow,
    },
  ],
  panelStats: [
    { value: 'DACH / EU', label: 'Kundenfokus' },
    { value: 'Cloud-agnostisch', label: 'Azure, AWS, Google Cloud' },
    { value: 'Founder-led', label: 'Senior-Verantwortung' },
  ],
  shiftEyebrow: 'Warum Ponte existiert',
  shiftTitle:
    'Das Produkt ist bereits stark. Die Service-Schicht ist der nächste Vorteil.',
  shiftLead:
    'Viele Hardwareunternehmen haben den schwierigsten Teil bereits: ein vertrauenswürdiges physisches Produkt im Feld. Die nächste Lücke liegt bei Sichtbarkeit, Kundenzugang, Serviceworkflow und Integration in Business-Systeme.',
  shiftParagraphs: [
    'Ponte schließt diese Lücke, ohne selbst Hersteller zu sein. Wir bauen die Connected-Product-Schicht um Produkte herum, die Ihr Unternehmen bereits entwickelt, verkauft, installiert und betreut.',
    'Das Ergebnis ist kein generisches Portal und keine Cloud-Demo. Es ist eine praktische Betriebsschicht, mit der Produkt-, Service- und Managementteams Gerätedaten täglich nutzen können.',
  ],
  modelEyebrow: 'Wie wir arbeiten',
  modelTitle: 'Praktisch starten, den Weg beweisen, dann skalieren.',
  modelLead:
    'Gute Connected-Product-Programme starten nicht mit einem riesigen Plattformbau. Sie starten mit einer klaren Produktlinie, echten Felddaten und einem wertvollen Serviceworkflow.',
  stages: [
    {
      eyebrow: '01',
      title: 'Connected Product Audit',
      text: 'Wir erfassen Produktlinie, Gerätedaten, Feldbedingungen, Nutzer, Serviceprozess, Business-Systeme und den ersten realistischen Wertbeitrag.',
      icon: Gauge,
    },
    {
      eyebrow: '02',
      title: 'Pilot mit echten Daten',
      text: 'Wir bauen einen schmalen, funktionierenden Weg: Signal ingestieren, in nutzbaren Kontext verwandeln und dem Team zeigen, das handeln muss.',
      icon: Wrench,
    },
    {
      eyebrow: '03',
      title: 'Serviceplattform-Fundament',
      text: 'Wir härten Identität, Rollen, Dashboards, Automationen, Reporting und Runbooks, damit das System Teil des Betriebs wird.',
      icon: CloudCog,
    },
  ],
  principlesEyebrow: 'Arbeitsprinzipien',
  principlesTitle: 'Eine Beratung für die schwierige Mitte.',
  principlesLead:
    'Ponte sitzt zwischen Hardware, Cloud, Analytics und Servicebetrieb. Das funktioniert nur, wenn der Einsatz praktisch bleibt und technisch verantwortet wird.',
  principles: [
    {
      title: 'Direkte Senior-Verantwortung',
      text: 'Sie arbeiten mit den Menschen, die Architektur und Umsetzung verantworten, nicht mit einer wechselnden Account-Schicht.',
      icon: ShieldCheck,
    },
    {
      title: 'Cloud-Pfad folgt Produktstrategie',
      text: 'Azure, AWS, Google Cloud, hybrid oder bestehende Infrastruktur: Die Architektur folgt Produkt und Betriebsmodell.',
      icon: CloudCog,
    },
    {
      title: 'Business-Systeme gehören zum Design',
      text: 'Gerätedaten sollen Service, CRM, ERP, BI und Kundenworkflows erreichen, statt in einem separaten Cloud-Silo zu enden.',
      icon: Workflow,
    },
    {
      title: 'Übergabe wird mitentwickelt',
      text: 'Runbooks, Monitoring, Kostenkontrolle und klare Verantwortungen sind Teil der Lieferung, nicht nachträgliche Aufräumarbeit.',
      icon: CheckCircle2,
    },
  ],
  ctaEyebrow: 'Start mit der installierten Basis',
}

const containerVariants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.09 } },
}

const itemVariants = {
  hidden: { opacity: 0, y: 18 },
  show: { opacity: 1, y: 0, transition: { duration: 0.45, ease: 'easeOut' } },
}

function getAboutCopy(locale: string) {
  return locale.startsWith('de') ? deCopy : enCopy
}

function readStringArray(raw: unknown): string[] {
  return Array.isArray(raw)
    ? raw.filter((item): item is string => typeof item === 'string')
    : []
}

function onSectionMouseMove(e: React.MouseEvent<HTMLElement>) {
  const rect = e.currentTarget.getBoundingClientRect()
  e.currentTarget.style.setProperty('--spot-x', `${e.clientX - rect.left}px`)
  e.currentTarget.style.setProperty('--spot-y', `${e.clientY - rect.top}px`)
}

function onCardMouseMove(e: React.MouseEvent<HTMLElement>) {
  const rect = e.currentTarget.getBoundingClientRect()
  e.currentTarget.style.setProperty('--mouse-x', `${e.clientX - rect.left}px`)
  e.currentTarget.style.setProperty('--mouse-y', `${e.clientY - rect.top}px`)
}

function CardSpotlight() {
  return (
    <>
      <div className="pointer-events-none absolute inset-0 z-0 rounded-xl bg-accent/[0.07] opacity-0 transition-opacity duration-200 group-hover:opacity-100" />
      <div
        className="pointer-events-none absolute inset-0 z-0 rounded-xl opacity-0 transition-opacity duration-200 group-hover:opacity-100"
        style={{
          background:
            'radial-gradient(260px circle at var(--mouse-x, 50%) var(--mouse-y, 50%), oklch(68% .095 52 / 0.2), transparent 70%)',
        }}
      />
    </>
  )
}

function AboutPage() {
  const location = useLocation()
  const locale = getLocaleFromPath(location.pathname)
  const copy = getAboutCopy(locale)

  return (
    <LandingPageLayout>
      <div className="ponte-service-page">
        <SectionScroller labels={copy.scrollerLabels}>
          <AboutHeroSection copy={copy} locale={locale} />
          <AboutShiftSection copy={copy} />
          <AboutModelSection copy={copy} />
          <AboutPrinciplesSection copy={copy} />
          <AboutCtaSection copy={copy} locale={locale} />
        </SectionScroller>
      </div>
    </LandingPageLayout>
  )
}

function AboutHeroSection({
  copy,
  locale,
}: {
  copy: AboutCopy
  locale: string
}) {
  const { t } = useTranslation('pages')

  return (
    <section
      className="ponte-service-section ponte-service-section--hero relative isolate flex min-h-[calc(100svh-4rem)] items-center overflow-hidden px-4 py-10 sm:px-6 lg:px-8"
      style={{ '--spot-x': '50%', '--spot-y': '50%' } as React.CSSProperties}
      onMouseMove={onSectionMouseMove}
    >
      <GridSpotlight baseOpacity={0.18} highlightOpacity={0.32} />
      <div className="relative z-10 mx-auto grid w-full max-w-7xl items-center gap-10 lg:grid-cols-[0.92fr_1.08fr]">
        <motion.div
          className="max-w-3xl py-10"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, ease: 'easeOut' }}
        >
          <motion.div
            className="mb-6 inline-flex items-center gap-2 rounded-full border border-border/70 bg-card/70 px-3 py-1.5 text-xs font-medium uppercase tracking-[0.14em] text-muted-foreground shadow-sm backdrop-blur-md dark:border-white/15 dark:bg-white/8 dark:text-white/75"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, delay: 0.1 }}
          >
            <Building2 className="h-3.5 w-3.5 text-accent" />
            {copy.heroBadge}
          </motion.div>

          <motion.h1
            className={cn(
              designSystem.typography.heading.h1,
              'max-w-4xl text-balance text-[clamp(2.8rem,7vw,5.5rem)] font-semibold leading-[0.92] tracking-normal text-foreground dark:text-white',
            )}
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.18, ease: 'easeOut' }}
          >
            {t('about.hero.titlePrefix')}{' '}
            <span className="text-accent">
              {t('about.hero.titleHighlight')}
            </span>
          </motion.h1>

          <motion.p
            className="mt-7 max-w-2xl text-pretty text-lg leading-8 text-muted-foreground md:text-xl dark:text-white/70"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.28 }}
          >
            {t('about.hero.description')}
          </motion.p>

          <motion.p
            className="mt-5 max-w-2xl text-base leading-7 text-muted-foreground dark:text-white/56"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.35 }}
          >
            {copy.heroLead}
          </motion.p>

          <motion.div
            className="mt-9 flex flex-col gap-3 sm:flex-row"
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, delay: 0.42 }}
          >
            <Link
              to={withLocalePath('/contact', locale)}
              className="landing-cta-primary inline-flex items-center justify-center gap-2 rounded-xl px-6 py-3 text-sm font-semibold"
            >
              {copy.heroPrimary}
              <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to={withLocalePath('/services/connect', locale)}
              className="landing-cta-secondary inline-flex items-center justify-center gap-2 rounded-xl px-6 py-3 text-sm font-semibold"
            >
              {copy.heroSecondary}
              <ArrowRight className="h-4 w-4" />
            </Link>
          </motion.div>
        </motion.div>

        <motion.div
          className="relative"
          initial={{ opacity: 0, y: 24, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.62, delay: 0.2, ease: 'easeOut' }}
        >
          <AboutOperatingPanel copy={copy} />
        </motion.div>
      </div>
    </section>
  )
}

function AboutOperatingPanel({ copy }: { copy: AboutCopy }) {
  return (
    <div className="relative overflow-hidden rounded-2xl border border-border/70 bg-card/70 p-3 shadow-2xl shadow-black/10 backdrop-blur-xl dark:border-white/12 dark:bg-black/20 dark:shadow-black/35">
      <div
        aria-hidden
        className="absolute inset-0 bg-[radial-gradient(circle_at_74%_18%,oklch(68%_.095_52/.18),transparent_34%)]"
      />
      <div className="relative rounded-xl border border-border/70 bg-background/82 p-5 dark:border-white/10 dark:bg-white/[0.035]">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-accent">
              {copy.panelTitle}
            </p>
            <h2 className="mt-3 max-w-md text-2xl font-semibold text-foreground dark:text-white">
              {copy.panelSubtitle}
            </h2>
          </div>
          <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1.5 text-xs font-semibold text-muted-foreground dark:border-white/12 dark:bg-white/8 dark:text-white/60">
            <MapPin className="h-3.5 w-3.5 text-accent" />
            Sarajevo / EU
          </div>
        </div>

        <div className="mt-8 grid gap-4">
          {copy.panelSteps.map((step, index) => (
            <div
              key={step.label}
              className="relative grid gap-4 rounded-xl border border-border/70 bg-card/70 p-4 sm:grid-cols-[3rem_1fr] dark:border-white/10 dark:bg-white/[0.045]"
            >
              {index < copy.panelSteps.length - 1 && (
                <div
                  aria-hidden
                  className="absolute left-[2.45rem] top-[4.2rem] hidden h-7 border-l border-dashed border-accent/40 sm:block"
                />
              )}
              <div className="flex h-12 w-12 items-center justify-center rounded-lg border border-accent/20 bg-accent/10 text-accent">
                <step.icon className="h-5 w-5" />
              </div>
              <div>
                <div className="flex items-center gap-3">
                  <span className="font-mono text-xs text-muted-foreground dark:text-white/35">
                    0{index + 1}
                  </span>
                  <h3 className="text-base font-semibold text-foreground dark:text-white">
                    {step.label}
                  </h3>
                </div>
                <p className="mt-2 text-sm leading-6 text-muted-foreground dark:text-white/58">
                  {step.value}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-5 grid gap-3 sm:grid-cols-3">
          {copy.panelStats.map((stat) => (
            <div
              key={stat.label}
              className="rounded-xl border border-border/70 bg-background/70 p-4 text-center dark:border-white/10 dark:bg-black/18"
            >
              <div className="text-lg font-semibold text-foreground dark:text-white">
                {stat.value}
              </div>
              <div className="mt-1 text-[0.68rem] font-semibold uppercase tracking-[0.16em] text-muted-foreground dark:text-white/40">
                {stat.label}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

function AboutShiftSection({ copy }: { copy: AboutCopy }) {
  const { t } = useTranslation('pages')
  const missionPoints = readStringArray(
    t('about.sections.mission.points', { returnObjects: true }),
  )
  const expertisePoints = readStringArray(
    t('about.sections.expertise.points', { returnObjects: true }),
  )
  const approachPoints = readStringArray(
    t('about.sections.approach.points', { returnObjects: true }),
  )
  const pillars: AboutPillar[] = [
    {
      key: 'mission',
      icon: Network,
      title: t('about.sections.mission.title'),
      description: t('about.sections.mission.description'),
      points: missionPoints,
    },
    {
      key: 'expertise',
      icon: DatabaseZap,
      title: t('about.sections.expertise.title'),
      description: t('about.sections.expertise.description'),
      points: expertisePoints,
    },
    {
      key: 'approach',
      icon: Workflow,
      title: t('about.sections.approach.title'),
      description: t('about.sections.approach.description'),
      points: approachPoints,
    },
  ]

  return (
    <section className="ponte-service-section ponte-service-section--deep relative flex min-h-[calc(100svh-4rem)] items-center overflow-hidden px-4 py-14 sm:px-6 lg:px-8">
      <div
        aria-hidden
        className="absolute inset-0 bg-[radial-gradient(circle_at_18%_64%,oklch(68%_.095_52/.12),transparent_40%)]"
      />

      <motion.div
        className="relative z-10 mx-auto grid w-full max-w-7xl gap-10 lg:grid-cols-[0.78fr_1.22fr] lg:items-center"
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: '-80px' }}
        variants={containerVariants}
      >
        <motion.div variants={itemVariants}>
          <span
            className={cn(
              designSystem.typography.display.eyebrow,
              'mb-3 block text-accent',
            )}
          >
            {copy.shiftEyebrow}
          </span>
          <h2 className="max-w-xl text-balance text-3xl font-semibold leading-tight tracking-normal text-foreground sm:text-4xl lg:text-5xl dark:text-white">
            {copy.shiftTitle}
          </h2>
          <p className="mt-6 max-w-xl text-lg leading-8 text-muted-foreground dark:text-white/66">
            {copy.shiftLead}
          </p>
          <div className="mt-6 space-y-4 text-base leading-8 text-muted-foreground dark:text-white/56">
            {copy.shiftParagraphs.map((paragraph) => (
              <p key={paragraph}>{paragraph}</p>
            ))}
          </div>
        </motion.div>

        <motion.div className="grid gap-4" variants={containerVariants}>
          {pillars.map((pillar, index) => (
            <motion.div
              key={pillar.key}
              variants={itemVariants}
              className="group relative overflow-hidden rounded-xl border border-border/70 bg-card/70 p-5 shadow-sm backdrop-blur-sm transition-all duration-300 hover:border-accent/40 dark:border-white/[0.08] dark:bg-white/[0.035]"
              style={
                {
                  '--mouse-x': '50%',
                  '--mouse-y': '50%',
                } as React.CSSProperties
              }
              onMouseMove={onCardMouseMove}
            >
              <CardSpotlight />
              <div className="relative z-10 grid gap-4 sm:grid-cols-[3rem_1fr]">
                <div className="flex h-12 w-12 items-center justify-center rounded-md bg-accent/10 text-accent transition-colors duration-300 group-hover:bg-accent group-hover:text-accent-foreground">
                  <pillar.icon className="h-5 w-5" />
                </div>
                <div>
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-xs text-muted-foreground dark:text-white/35">
                      0{index + 1}
                    </span>
                    <h3 className="text-lg font-semibold text-foreground dark:text-white">
                      {pillar.title}
                    </h3>
                  </div>
                  <p className="mt-2 text-sm leading-7 text-muted-foreground dark:text-white/58">
                    {pillar.description}
                  </p>
                  <div className="mt-4 grid gap-2">
                    {pillar.points.map((point) => (
                      <div
                        key={point}
                        className="flex gap-2 text-sm leading-6 text-muted-foreground dark:text-white/55"
                      >
                        <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
                        <span>{point}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  )
}

function AboutModelSection({ copy }: { copy: AboutCopy }) {
  return (
    <section
      className="ponte-service-section ponte-service-section--mesh relative flex min-h-[calc(100svh-4rem)] items-center overflow-hidden px-4 py-14 sm:px-6 lg:px-8"
      style={{ '--spot-x': '50%', '--spot-y': '50%' } as React.CSSProperties}
      onMouseMove={onSectionMouseMove}
    >
      <GridSpotlight gridSize={56} baseOpacity={0.16} highlightOpacity={0.3} />
      <motion.div
        className="relative z-10 mx-auto w-full max-w-7xl"
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: '-80px' }}
        variants={containerVariants}
      >
        <motion.div
          className="mx-auto max-w-3xl text-center"
          variants={itemVariants}
        >
          <span
            className={cn(
              designSystem.typography.display.eyebrow,
              'mb-3 block text-accent',
            )}
          >
            {copy.modelEyebrow}
          </span>
          <h2 className="text-balance text-3xl font-semibold leading-tight tracking-normal text-foreground sm:text-4xl lg:text-5xl dark:text-white">
            {copy.modelTitle}
          </h2>
          <p className="mt-5 text-base leading-8 text-muted-foreground dark:text-white/60">
            {copy.modelLead}
          </p>
        </motion.div>

        <div className="mt-12 grid gap-4 lg:grid-cols-3">
          {copy.stages.map((stage) => (
            <motion.div
              key={stage.title}
              variants={itemVariants}
              className="group relative overflow-hidden rounded-2xl border border-border/70 bg-card/75 p-6 shadow-sm backdrop-blur-sm transition hover:border-accent/45 dark:border-white/[0.09] dark:bg-white/[0.035]"
              style={
                {
                  '--mouse-x': '50%',
                  '--mouse-y': '50%',
                } as React.CSSProperties
              }
              onMouseMove={onCardMouseMove}
            >
              <CardSpotlight />
              <div className="relative z-10">
                <div className="mb-10 flex items-center justify-between">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-accent/20 bg-accent/10 text-accent">
                    <stage.icon className="h-5 w-5" />
                  </div>
                  <span className="rounded-full border border-border/70 bg-background/70 px-3 py-1 font-mono text-xs text-muted-foreground dark:border-white/10 dark:bg-black/20 dark:text-white/35">
                    {stage.eyebrow}
                  </span>
                </div>
                <h3 className="text-2xl font-semibold text-foreground dark:text-white">
                  {stage.title}
                </h3>
                <p className="mt-4 text-sm leading-7 text-muted-foreground dark:text-white/58">
                  {stage.text}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </motion.div>
    </section>
  )
}

function AboutPrinciplesSection({ copy }: { copy: AboutCopy }) {
  return (
    <section className="ponte-service-section ponte-service-section--deep relative flex min-h-[calc(100svh-4rem)] items-center overflow-hidden px-4 py-14 sm:px-6 lg:px-8">
      <div
        aria-hidden
        className="absolute inset-0 bg-[radial-gradient(circle_at_82%_28%,oklch(68%_.095_52/.11),transparent_38%)]"
      />
      <motion.div
        className="relative z-10 mx-auto grid w-full max-w-7xl gap-10 lg:grid-cols-[0.82fr_1.18fr] lg:items-center"
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: '-80px' }}
        variants={containerVariants}
      >
        <motion.div variants={itemVariants}>
          <span
            className={cn(
              designSystem.typography.display.eyebrow,
              'mb-3 block text-accent',
            )}
          >
            {copy.principlesEyebrow}
          </span>
          <h2 className="max-w-xl text-balance text-3xl font-semibold leading-tight tracking-normal text-foreground sm:text-4xl lg:text-5xl dark:text-white">
            {copy.principlesTitle}
          </h2>
          <p className="mt-6 max-w-xl text-lg leading-8 text-muted-foreground dark:text-white/64">
            {copy.principlesLead}
          </p>
        </motion.div>

        <motion.div
          className="grid gap-4 sm:grid-cols-2"
          variants={containerVariants}
        >
          {copy.principles.map((principle) => (
            <motion.div
              key={principle.title}
              variants={itemVariants}
              className="group relative min-h-56 overflow-hidden rounded-xl border border-border/70 bg-card/70 p-5 shadow-sm backdrop-blur-sm transition hover:border-accent/40 dark:border-white/[0.08] dark:bg-white/[0.035]"
              style={
                {
                  '--mouse-x': '50%',
                  '--mouse-y': '50%',
                } as React.CSSProperties
              }
              onMouseMove={onCardMouseMove}
            >
              <CardSpotlight />
              <div className="relative z-10 flex h-full flex-col">
                <div className="mb-8 flex h-11 w-11 items-center justify-center rounded-lg border border-accent/20 bg-accent/10 text-accent">
                  <principle.icon className="h-5 w-5" />
                </div>
                <h3 className="text-xl font-semibold text-foreground dark:text-white">
                  {principle.title}
                </h3>
                <p className="mt-4 text-sm leading-7 text-muted-foreground dark:text-white/58">
                  {principle.text}
                </p>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  )
}

function AboutCtaSection({
  copy,
  locale,
}: {
  copy: AboutCopy
  locale: string
}) {
  const { t } = useTranslation('pages')

  return (
    <section
      className="ponte-service-section ponte-service-section--cta relative isolate flex min-h-[calc(100svh-4rem)] items-center overflow-hidden px-4 py-16 sm:px-6 lg:px-8"
      style={{ '--spot-x': '50%', '--spot-y': '50%' } as React.CSSProperties}
      onMouseMove={onSectionMouseMove}
    >
      <GridSpotlight gridSize={56} baseOpacity={0.16} highlightOpacity={0.3} />
      <motion.div
        className="relative z-10 mx-auto max-w-4xl text-center"
        initial={{ opacity: 0, y: 22 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-80px' }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
      >
        <span
          className={cn(
            designSystem.typography.display.eyebrow,
            'mb-4 block text-accent',
          )}
        >
          {copy.ctaEyebrow}
        </span>
        <h2 className="text-balance text-4xl font-semibold leading-tight tracking-normal text-foreground md:text-6xl dark:text-white">
          {t('about.cta.title')}
        </h2>
        <p className="mx-auto mt-6 max-w-2xl text-lg leading-8 text-muted-foreground dark:text-white/64">
          {t('about.cta.description')}
        </p>
        <div className="mt-9 flex flex-wrap justify-center gap-3">
          <Link
            to={withLocalePath('/contact', locale)}
            className="landing-cta-primary inline-flex min-h-11 items-center rounded-md px-5 py-3 text-sm font-semibold"
          >
            {t('about.cta.primary')}
            <ArrowRight className="ml-2 h-4 w-4" />
          </Link>
          <Link
            to={withLocalePath('/industries', locale)}
            className="landing-cta-secondary inline-flex min-h-11 items-center rounded-md px-5 py-3 text-sm font-semibold"
          >
            {t('about.cta.secondary')}
          </Link>
        </div>
      </motion.div>
    </section>
  )
}
